<script setup>
import { computed } from 'vue'
import { ArrowLeftRight, Clock, Eye, Footprints } from '@lucide/vue'

import AuditCard from './AuditCard.vue'
import AuditItems from './AuditItems.vue'
import AuditPositions from './AuditPositions.vue'
import EvidenceList from './EvidenceList.vue'
import { useAuditText } from './audit-text.js'
import { asArray, characterName, eventKinds, eventRows, itemName, placeName } from './run-observer.js'

const props = defineProps({
  pack: { type: Object, required: true },
  branch: { type: Object, required: true },
  sources: { type: Map, required: true },
})

const KIND_ICONS = { move: Footprints, time: Clock, transfer: ArrowLeftRight, witness: Eye }

const { tr, enumLabel, storyTimeLabel } = useAuditText()
const rows = computed(() => eventRows(props.branch).map((event) => ({
  ...event,
  kinds: eventKinds(event),
  summary: summaryOf(event),
  audience: asArray(event.audience_ids).map((id) => characterName(props.pack, id)).join(tr('separator')),
})))

function moveLabel(move) {
  const path = asArray(move.path).map((id) => placeName(props.pack, id)).join(' → ')
  return `${characterName(props.pack, move.actor_id)}${path ? ` → ${path}` : ''}`
}

function transferLabel(transfer) {
  const holder = transfer.holder_id ? characterName(props.pack, transfer.holder_id) : tr('timeline.unheld')
  return `${transfer.item_id ? itemName(props.pack, transfer.item_id) : '—'} → ${holder}`
}

/** 一句摘要：有正文用正文，否则把移动、转移、时间变化拼成一句。 */
function summaryOf(event) {
  if (event.text) return event.text
  const parts = [...asArray(event.moves).map(moveLabel), ...asArray(event.transfers).map(transferLabel)]
  if (event.time) parts.push(storyTimeLabel(event.time))
  return parts.join(' · ') || enumLabel('timeline.kinds', eventKinds(event)[0])
}
</script>

<template>
  <div class="au-tab">
    <AuditPositions :pack="pack" :branch="branch" />
    <div class="au-split">
      <AuditCard :title="tr('timeline.events')">
        <template #meta>{{ rows.length }}</template>
        <ol v-if="rows.length" class="au-timeline" data-testid="observer-events">
          <li v-for="event in rows" :key="event.event_id">
            <details class="au-event">
              <summary>
                <span class="au-turnmark">#{{ event.seq || '—' }}</span>
                <span class="au-event__icons">
                  <span v-for="kind in event.kinds" :key="kind" role="img" :title="enumLabel('timeline.kinds', kind)" :aria-label="enumLabel('timeline.kinds', kind)">
                    <component :is="KIND_ICONS[kind]" :size="14" aria-hidden="true" />
                  </span>
                </span>
                <span class="au-event__text" :title="event.summary">{{ event.summary }}</span>
                <span class="au-event__place">{{ placeName(pack, event.location_id) }}</span>
                <span class="au-event__evidence">{{ tr('sources.evidence', { count: asArray(event.evidence_ids).length }) }}</span>
              </summary>
              <div class="au-event__body">
                <dl class="au-kv">
                  <dt>{{ tr('timeline.audience') }}</dt>
                  <dd>{{ event.audience || '—' }}</dd>
                  <template v-if="asArray(event.moves).length">
                    <dt>{{ tr('timeline.moves') }}</dt>
                    <dd>{{ event.moves.map(moveLabel).join(tr('separator')) }}</dd>
                  </template>
                  <template v-if="event.time">
                    <dt>{{ tr('timeline.timeChange') }}</dt>
                    <dd>{{ storyTimeLabel(event.time) }}</dd>
                  </template>
                  <template v-if="asArray(event.transfers).length">
                    <dt>{{ tr('timeline.transfers') }}</dt>
                    <dd>{{ event.transfers.map(transferLabel).join(tr('separator')) }}</dd>
                  </template>
                </dl>
                <EvidenceList v-if="asArray(event.evidence_ids).length" :record="event" :sources="sources" />
              </div>
            </details>
          </li>
        </ol>
        <p v-else class="au-empty">{{ tr('empty') }}</p>
      </AuditCard>
      <AuditItems :pack="pack" :branch="branch" />
    </div>
  </div>
</template>
